import React, { useEffect, useState, useRef } from 'react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../api/axios';
import { Plus, Trash2, Edit2, Image, X, ChevronLeft, ChevronRight } from 'lucide-react';

const CATEGORIES = [
  { key: 'haber', label: 'Haber' },
  { key: 'egitim', label: 'Eğitim Faaliyeti' },
  { key: 'duyuru', label: 'Duyuru' },
];

const ImageSlider = ({ images }) => {
  const [idx, setIdx] = useState(0);
  if (!images || images.length === 0) return null;
  if (images.length === 1) return <img src={images[0].image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <img src={images[idx].image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      <button onClick={e => { e.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); }}
        style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ChevronLeft size={14} />
      </button>
      <button onClick={e => { e.stopPropagation(); setIdx(i => (i + 1) % images.length); }}
        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ChevronRight size={14} />
      </button>
      <div style={{ position: 'absolute', bottom: 6, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 4 }}>
        {images.map((_, i) => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: i === idx ? 'white' : 'rgba(255,255,255,0.5)' }} />)}
      </div>
    </div>
  );
};

export default function AdminAnnouncements() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [confirmModal, setConfirmModal] = React.useState({ open: false, action: null, title: '', message: '' });
  const showConfirm = (title, message, action) => setConfirmModal({ open: true, title, message, action });
  const closeConfirm = () => setConfirmModal({ open: false, action: null, title: '', message: '' });
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', category: 'haber', is_published: true });
  const [newImages, setNewImages] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => api.get('/announcements').then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ title: '', content: '', category: 'haber', is_published: true });
    setNewImages([]); setNewPreviews([]); setExistingImages([]); setRemovedImages([]);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ title: item.title, content: item.content || '', category: item.category, is_published: item.is_published });
    setExistingImages(item.images || []);
    setNewImages([]); setNewPreviews([]); setRemovedImages([]);
    setShowModal(true);
  };

  const handleImages = (e) => {
    const files = Array.from(e.target.files);
    const oversized = files.filter(f => f.size > 20 * 1024 * 1024);
    if (oversized.length > 0) { alert('Resim boyutu 20MB\'dan büyük olamaz'); return; }
    const total = existingImages.length - removedImages.length + newImages.length + files.length;
    if (total > 3) { alert('En fazla 3 resim eklenebilir'); return; }
    setNewImages(prev => [...prev, ...files]);
    setNewPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };

  const removeNewImage = (i) => {
    setNewImages(prev => prev.filter((_, idx) => idx !== i));
    setNewPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const removeExistingImage = (img) => {
    setRemovedImages(prev => [...prev, img.id]);
    setExistingImages(prev => prev.filter(x => x.id !== img.id));
  };

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => formData.append(k, v));
    newImages.forEach(f => formData.append('images', f));
    if (removedImages.length > 0) formData.append('remove_images', JSON.stringify(removedImages));
    try {
      if (editing) await api.put('/announcements/' + editing.id, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      else await api.post('/announcements', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowModal(false); load();
    } catch(e) { alert('Hata: ' + e.message); }
    setSaving(false);
  };

  const deleteItem = (id) => {
    showConfirm('İçeriği Sil', 'Bu içeriği silmek istediğinize emin misiniz?', async () => {
      await api.delete('/announcements/' + id);
      load();
      closeConfirm();
    });
  };

  const catColor = { haber: '#007aff', egitim: '#34c759', duyuru: '#ff9500' };
  const catLabel = { haber: 'Haber', egitim: 'Eğitim', duyuru: 'Duyuru' };
  const totalSlots = existingImages.length + newImages.length;

  return (
    <AdminLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontWeight: 700, fontSize: 22, color: '#1a1a2e' }}>Eğitim & Faaliyetler</h2>
        <button onClick={openNew} style={{ background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Plus size={16} /> Yeni İçerik
        </button>
      </div>

      {items.length === 0 ? (
        <div style={{ background: 'rgba(255,255,255,0.75)', borderRadius: 16, padding: 48, textAlign: 'center', color: '#9ca3af' }}>Henüz içerik yok.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {items.map(item => (
            <div key={item.id} style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
              {(item.images && item.images.length > 0) && (
                <div style={{ height: 160, overflow: 'hidden', position: 'relative' }}>
                  <ImageSlider images={item.images} />
                </div>
              )}
              <div style={{ padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ background: `rgba(${catColor[item.category] === '#007aff' ? '0,122,255' : catColor[item.category] === '#34c759' ? '52,199,89' : '255,149,0'},0.1)`, color: catColor[item.category] || '#6b7280', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
                    {catLabel[item.category] || item.category}
                  </span>
                  {item.images && item.images.length > 1 && (
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>🖼 {item.images.length} resim</span>
                  )}
                  {!item.is_published && <span style={{ background: 'rgba(0,0,0,0.05)', color: '#9ca3af', borderRadius: 20, padding: '3px 10px', fontSize: 11 }}>Taslak</span>}
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e', marginBottom: 6 }}>{item.title}</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 14, height: 36, overflow: 'hidden' }}>{item.content}</div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => openEdit(item)} style={{ background: 'rgba(0,122,255,0.08)', color: '#007aff', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Edit2 size={12} /> Düzenle
                  </button>
                  {isAdmin && <button onClick={() => deleteItem(item.id)} style={{ background: 'rgba(239,68,68,0.08)', color: '#ef4444', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <Trash2 size={13} />
                  </button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 580, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 32px 80px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '24px 28px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 18, color: '#1a1a2e' }}>{editing ? 'İçerik Düzenle' : 'Yeni İçerik'}</div>
                <button onClick={() => setShowModal(false)} style={{ background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={15} /></button>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13, color: '#6b7280' }}>BAŞLIK *</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13, color: '#6b7280' }}>KATEGORİ</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {CATEGORIES.map(cat => (
                    <button key={cat.key} onClick={() => setForm({...form, category: cat.key})}
                      style={{ flex: 1, padding: '9px', border: `2px solid ${form.category === cat.key ? '#007aff' : '#e5e7eb'}`, borderRadius: 10, background: form.category === cat.key ? 'rgba(0,122,255,0.06)' : 'white', cursor: 'pointer', fontWeight: 600, fontSize: 12, color: form.category === cat.key ? '#007aff' : '#6b7280' }}>
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13, color: '#6b7280' }}>İÇERİK</label>
                <textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={4}
                  style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>

              {/* Görseller */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 13, color: '#6b7280' }}>GÖRSELLER (En fazla 3)</label>
                <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleImages} style={{ display: 'none' }} />

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {/* Mevcut resimler */}
                  {existingImages.map(img => (
                    <div key={img.id} style={{ position: 'relative', width: 100, height: 80, borderRadius: 10, overflow: 'hidden' }}>
                      <img src={img.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button onClick={() => removeExistingImage(img)}
                        style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <X size={11} />
                      </button>
                    </div>
                  ))}

                  {/* Yeni resim önizlemeleri */}
                  {newPreviews.map((preview, i) => (
                    <div key={i} style={{ position: 'relative', width: 100, height: 80, borderRadius: 10, overflow: 'hidden', border: '2px solid #007aff' }}>
                      <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button onClick={() => removeNewImage(i)}
                        style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <X size={11} />
                      </button>
                    </div>
                  ))}

                  {/* Resim ekle butonu */}
                  {totalSlots < 3 && (
                    <button onClick={() => fileRef.current.click()}
                      style={{ width: 100, height: 80, border: '2px dashed #e5e7eb', borderRadius: 10, background: '#fafafa', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#9ca3af' }}>
                      <Image size={20} strokeWidth={1.5} />
                      <span style={{ fontSize: 11, fontWeight: 500 }}>Ekle</span>
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>{totalSlots}/3 resim</div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24, padding: '12px 14px', background: form.is_published ? 'rgba(52,199,89,0.06)' : 'rgba(0,0,0,0.03)', borderRadius: 10, cursor: 'pointer', border: `1.5px solid ${form.is_published ? 'rgba(52,199,89,0.3)' : '#e5e7eb'}` }}
                onClick={() => setForm({...form, is_published: !form.is_published})}>
                <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${form.is_published ? '#34c759' : '#d1d5db'}`, background: form.is_published ? '#34c759' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {form.is_published && <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>✓</span>}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: form.is_published ? '#34c759' : '#374151' }}>Yayınla</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>Kullanıcılar bu içeriği görebilir</div>
                </div>
              </div>
            </div>

            <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', border: '1.5px solid #e5e7eb', borderRadius: 10, background: 'white', cursor: 'pointer', fontWeight: 600 }}>İptal</button>
              <button onClick={save} disabled={saving || !form.title.trim()} style={{ padding: '10px 28px', background: saving ? '#9ca3af' : 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                {saving ? 'Kaydediliyor...' : editing ? 'Güncelle' : 'Yayınla'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => { confirmModal.action && confirmModal.action(); }}
        onCancel={closeConfirm}
        confirmText="Evet, Sil"
      />
    </AdminLayout>
  );
}
